import React, { useState, useEffect, useMemo } from 'react'
import { FormApi } from 'final-form'
import { Form } from 'react-final-form'
import validate from 'validate.js'
import {
  getCheckboxState,
  NOT_VISIBLE_CHECKED,
  VISIBLE_CHECKED,
} from '@mc/gdpr'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { api } from '@mc/legacy-api'
import { paths } from '@mc/paths'
import { useStaticData } from '@mc/static-data-react'
import { NO_HUBSPOT_EMAIL_KEYWORD } from '../constants'
import {
  identifyFormSubmitted,
  trackFormSubmitted,
  trackEnterpriseSalesLeadSuccess,
  trackEnterpriseSalesLeadFail,
} from '../events'
import i18nInstance from '../i18n/i18nInstance'
import {
  appendHubspotSnippet,
  getLeadEmail,
  handlePostToHubspotV3,
} from '../utils'
import { PostToHubspotV3Props } from '../utils/postToHubspot'
import { FormBody } from './FormBodyV3'

const ContactFormComponent = ({
  envName,
  onClickSubmit,
}: {
  envName: string
  onClickSubmit?: () => void
}) => {
  const { t } = useTranslation('@mc/enterprise-contact-form')
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState(false)
  const [loading, setLoading] = useState(false)
  const [userEmail, setUserEmail] = useState(getLeadEmail())

  const { isLoggedIn } = useStaticData<{ isLoggedIn: boolean }>()

  const emailCapture = getCheckboxState()
  const emailCaptureCheckboxInitialValue = [
    NOT_VISIBLE_CHECKED,
    VISIBLE_CHECKED,
  ].includes(emailCapture)

  const leadEmailSubmittedHandler = () => {
    const email = getLeadEmail()
    if (email) {
      setUserEmail(email)
    }
  }

  useEffect(() => {
    appendHubspotSnippet()

    document.addEventListener('leadEmailSubmitted', leadEmailSubmittedHandler)

    return () => {
      document.removeEventListener(
        'leadEmailSubmitted',
        leadEmailSubmittedHandler,
      )
    }
  }, [])

  const CONSTRAINTS = {
    firstName: {
      presence: { message: t('groupForm.seats.constraints.firstName') },
    },
    lastName: {
      presence: { message: t('groupForm.seats.constraints.lastName') },
    },
    email: {
      presence: { message: t('groupForm.seats.constraints.email') },
      email: { message: t('groupForm.seats.constraints.emailInvalid') },
    },
    jobTitle: {
      presence: { message: t('groupForm.seats.constraints.jobTitle') },
    },
    organizationName: {
      presence: { message: t('groupForm.seats.constraints.organizationName') },
    },
    useCase: {
      presence: { message: t('groupForm.seats.constraints.useCase') },
    },
    whenAreYouLookingToPurchase: {
      presence: {
        message: t('groupForm.seats.constraints.whenAreYouLookingToPurchase'),
      },
    },
    gdprOptIn: {
      inclusion: {
        within: [true],
        message: t('groupForm.seats.constraints.gdprOptIn'),
      },
    },
  }

  const timeNow = useMemo(() => Date.now(), [])

  const getDefaultEmail = () => {
    if (userEmail) return userEmail

    return ['development', 'test', 'staging'].includes(envName)
      ? `test-${NO_HUBSPOT_EMAIL_KEYWORD}-${envName}-at-${timeNow}@test.com`
      : null
  }

  const handleValueChange = (
    role: number,
    form: FormApi<PostToHubspotV3Props, Partial<PostToHubspotV3Props>>,
    field: keyof PostToHubspotV3Props,
  ) => {
    form.change(field, `${role}`)
  }

  const validateForm = (values: PostToHubspotV3Props) =>
    validate(values, CONSTRAINTS, { fullMessages: false })

  const onSubmit = (lead: PostToHubspotV3Props) => {
    const {
      firstName,
      lastName,
      email,
      organizationName,
      jobTitle,
      employeeSize,
      useCase,
      gdprOptIn,
    } = lead

    setLoading(true)
    identifyFormSubmitted({
      firstName,
      lastName,
      email,
      organizationName,
      jobTitle,
      employeeSize,
      useCase,
    })
    trackFormSubmitted({
      firstName,
      lastName,
      email,
      organizationName,
      jobTitle,
      employeeSize,
      useCase,
      gdprOptIn,
      isLoggedIn,
    })
    setSubmitted(false)
    setErrors(false)

    const postToMCDb = api
      .post('/enterprise_sales_leads', { lead })
      .then(() => {
        // Successfully created an entry in /enterprise_sales_leads
        trackEnterpriseSalesLeadSuccess({
          firstName,
          lastName,
          email,
          organizationName,
          jobTitle,
          employeeSize,
          useCase,
          gdprOptIn,
        })
      })
      .catch((error) => {
        // Failure response on /enterprise_sales_leads call
        // Example of a 422 response: {"organization_name":["can't be blank"]}
        trackEnterpriseSalesLeadFail({
          firstName,
          lastName,
          email,
          organizationName,
          jobTitle,
          employeeSize,
          useCase,
          gdprOptIn,
          error: error || error?.status,
        })

        throw error
      })

    return Promise.all([postToMCDb, handlePostToHubspotV3(lead)])
      .then(() => {
        setSubmitted(true)
        onClickSubmit?.()
        window.location.href = paths.enterpriseThankYou
      })
      .catch(() => setErrors(true))
      .finally(() => setLoading(false))
  }

  return (
    <Form
      initialValues={{
        gdprOptIn: emailCaptureCheckboxInitialValue,
      }}
      validate={validateForm}
      onSubmit={onSubmit}
      render={({ handleSubmit, form, valid }) => {
        const formBodyProps = {
          form,
          errors,
          loading,
          submitted,
          onSubmit: handleSubmit,
          handleValueChange,
          defaultEmail: getDefaultEmail() ?? '',
          valid,
        }

        return <FormBody {...formBodyProps} />
      }}
    />
  )
}

export const ContactForm = withI18nContext(i18nInstance)(ContactFormComponent)
